import { ChevronRight } from "lucide-react";
import { ActionIcon, GitHubIcon } from "@insights/ui";
import { Fragment, useId } from "react";
import { parseBuildTargets } from "../helpers/build-graph-layout";
import { getGitHubWorkflowUrl } from "../helpers/get-github-workflow-url";
import type { Build, BuildListItem } from "../types/build";
import { BuildStatusBadge } from "./BuildStatusBadge";

type BuildListRowsProps = {
  expandedGroupIds: ReadonlySet<string>;
  items: BuildListItem[];
  onGroupExpandedChange: (groupId: string, expanded: boolean) => void;
  onOpenBuild: (buildId: string) => void;
};

type BuildRowProps = {
  build: Build;
  grouped?: boolean;
  interactive?: boolean;
  onOpenBuild: (buildId: string) => void;
};

function statusForBuild(build: Build) {
  if (!build.ended_at) {
    return "running" as const;
  }

  return build.success ? ("passed" as const) : ("failed" as const);
}

function GitHubWorkflowAction({ build }: { build: Build }) {
  const workflowUrl = getGitHubWorkflowUrl(build);
  if (!workflowUrl) {
    return null;
  }

  return (
    <ActionIcon
      href={workflowUrl}
      icon={<GitHubIcon />}
      label="View GitHub workflow"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      rel="noreferrer"
      size="small"
      target="_blank"
    />
  );
}

function MobileBuildTargets({ builds }: { builds: Build[] }) {
  const targets = Array.from(
    new Set(builds.flatMap((build) => parseBuildTargets(build.context_json))),
  );
  if (!targets.length) {
    return null;
  }

  return <small className="build-targets">{targets.join(", ")}</small>;
}

function BuildRow({
  build,
  grouped = false,
  interactive = true,
  onOpenBuild,
}: BuildRowProps) {
  const openBuild = () => onOpenBuild(build.id);

  return (
    <tr
      aria-label={`Open build ${build.repository ?? build.branch_or_tag}`}
      className={`build-row${grouped ? " grouped-build-row" : ""}`}
      onClick={openBuild}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openBuild();
        }
      }}
      role="link"
      tabIndex={interactive ? 0 : -1}
    >
      <td>
        <div
          className={`build-row-cell${grouped ? " build-group-leading" : ""}`}
        >
          {grouped && (
            <ChevronRight
              aria-hidden="true"
              className="build-group-chevron build-group-chevron-placeholder"
            />
          )}
          <BuildStatusBadge status={statusForBuild(build)} />
        </div>
      </td>
      <td>
        <div className="build-row-cell build-repository">
          {grouped ? (
            <span className="grouped-build-mobile-started">
              {new Date(build.started_at).toLocaleString()}
            </span>
          ) : (
            <>
              <strong>{build.repository ?? "Local build"}</strong>
              <span>{build.branch_or_tag}</span>
            </>
          )}
          <MobileBuildTargets builds={[build]} />
          <span className="build-mobile-workflow">
            <GitHubWorkflowAction build={build} />
          </span>
        </div>
      </td>
      <td>
        <div className="build-row-cell">
          <code>{build.environment ?? "—"}</code>
        </div>
      </td>
      <td>
        <div className="build-row-cell">{build.actor_name ?? "Unknown"}</div>
      </td>
      <td>
        <div className="build-row-cell build-started">
          <span>{new Date(build.started_at).toLocaleString()}</span>
          <GitHubWorkflowAction build={build} />
        </div>
      </td>
    </tr>
  );
}

type BuildGroupRowsProps = {
  expanded: boolean;
  item: Extract<BuildListItem, { kind: "group" }>;
  onExpandedChange: (expanded: boolean) => void;
  onOpenBuild: (buildId: string) => void;
};

function BuildGroupRows({
  expanded,
  item,
  onExpandedChange,
  onOpenBuild,
}: BuildGroupRowsProps) {
  const contentId = useId();
  const firstBuild = item.builds[0];
  const latestBuild = item.builds.at(-1);
  if (!firstBuild || !latestBuild) {
    return null;
  }

  const toggleGroup = () => onExpandedChange(!expanded);

  return (
    <>
      <tbody className="build-group">
        <tr className="build-group-row" onClick={toggleGroup}>
          <td>
            <div className="build-group-leading">
              <ActionIcon
                aria-controls={contentId}
                aria-expanded={expanded}
                color="accent"
                icon={
                  <ChevronRight
                    className={`build-group-chevron${expanded ? " expanded" : ""}`}
                  />
                }
                label={`Toggle build group ${item.group_id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleGroup();
                }}
                size="compact"
              />
              <BuildStatusBadge status={item.status} />
            </div>
          </td>
          <td>
            <span className="build-repository">
              <strong>{latestBuild.repository ?? "Local build"}</strong>
              <span className="build-group-branch-details">
                <span>{latestBuild.branch_or_tag}</span>
                <MobileBuildTargets builds={item.builds} />
              </span>
              <span className="build-mobile-workflow">
                <GitHubWorkflowAction build={latestBuild} />
              </span>
            </span>
          </td>
          <td>
            <code>{latestBuild.environment ?? "—"}</code>
          </td>
          <td>{latestBuild.actor_name ?? "Unknown"}</td>
          <td>
            <div className="build-started">
              <span>{new Date(firstBuild.started_at).toLocaleString()}</span>
              <GitHubWorkflowAction build={latestBuild} />
            </div>
          </td>
        </tr>
      </tbody>
      <tbody
        className="build-group-contents"
        data-state={expanded ? "open" : "closed"}
        id={contentId}
      >
        {item.builds.map((build) => (
          <BuildRow
            build={build}
            grouped
            interactive={expanded}
            key={build.id}
            onOpenBuild={onOpenBuild}
          />
        ))}
      </tbody>
    </>
  );
}

export function BuildListRows({
  expandedGroupIds,
  items,
  onGroupExpandedChange,
  onOpenBuild,
}: BuildListRowsProps) {
  return items.map((item) =>
    item.kind === "group" ? (
      <BuildGroupRows
        expanded={expandedGroupIds.has(item.group_id)}
        item={item}
        key={`group:${item.group_id}`}
        onExpandedChange={(expanded) =>
          onGroupExpandedChange(item.group_id, expanded)
        }
        onOpenBuild={onOpenBuild}
      />
    ) : (
      <Fragment key={`build:${item.build.id}`}>
        <tbody>
          <BuildRow build={item.build} onOpenBuild={onOpenBuild} />
        </tbody>
      </Fragment>
    ),
  );
}
